import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";

// Accounts we own besides the treasuries (guide 1.2). What they hold is left
// out of every customer figure. Read and written through lp-api, which passes
// it on to the signing server; the server adds the admin's email.
// Styles are inline: the admin page loads this pack's JS but not its CSS.

const card = {
  background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "8px",
  padding: "20px 24px", marginBottom: "20px",
};
const h2 = { fontSize: "16px", margin: "0 0 6px" };
const note = { fontSize: "13px", color: colors.gray600, margin: "0 0 12px" };
const help = { fontSize: "12px", color: colors.gray600, marginTop: "4px" };
const label = { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "4px" };
const input = {
  width: "100%", maxWidth: "260px", padding: "7px 10px", fontSize: "14px",
  border: `1px solid ${colors.gray400}`, borderRadius: "6px", boxSizing: "border-box",
};
const th = {
  textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px",
  color: colors.gray600, padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap",
};
const td = { padding: "8px 10px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
const mono = { fontFamily: "monospace", fontSize: "12px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "16px" };
const btn = (disabled) => ({
  background: colors.primary, color: "#fff", border: `1px solid ${colors.primary}`, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = (disabled) => ({ ...btn(disabled), background: "#fff", color: colors.primary });
const linkBtn = {
  background: "none", border: "none", padding: 0, color: colors.primary, cursor: "pointer",
  fontSize: "13px", textDecoration: "underline", marginLeft: "10px",
};
const TONES = { good: { bg: "#d4edda", fg: "#155724" }, bad: { bg: "#f8d7da", fg: "#721c24" } };
const Banner = ({ tone, children }) => (
  <div role="status" style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px" }}>
    {children}
  </div>
);

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

export const REASONS = {
  fees: "Fees",
  payout: "Payouts",
  dev: "Development",
  test_customer: "Test customer",
  other: "Other",
};
const ACTIONS = { add: "Added", update: "Changed", remove: "Removed" };

// Checks the add form. Returns an error message, or null when it can be sent.
export const walletError = (form) => {
  if (!/^0\.0\.\d{1,12}$/.test(form.accountId.trim())) return "Enter a Hedera account ID like 0.0.123456.";
  if (!REASONS[form.reason]) return "Pick a reason.";
  return null;
};

// "Fees → Payouts, note none → refunds": only what changed.
export const describeChange = (before, after) => {
  const show = (k, v) => (v == null || v === "" ? "none" : k === "reason" ? REASONS[v] || v : String(v));
  return ["reason", "note"]
    .filter((k) => show(k, (before || {})[k]) !== show(k, (after || {})[k]))
    .map((k) => `${k === "reason" ? "Reason" : "Note"} ${show(k, (before || {})[k])} → ${show(k, (after || {})[k])}`)
    .join(", ");
};

const EMPTY_FORM = { accountId: "", reason: "", note: "" };

const InternalWallets = ({ listUrl, saveUrl, removeUrl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null); // { tone, text }
  const [form, setForm] = useState(EMPTY_FORM);
  const [removing, setRemoving] = useState(null); // { accountId, note }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(listUrl);
      if (res?.success) {
        setData(res.data);
        setLoadError(null);
      } else {
        setLoadError(res?.error || "Couldn't load the internal wallets.");
      }
    } catch (e) {
      setLoadError("Couldn't load the internal wallets.");
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  const send = async (url, body, done) => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await postRequest(url, body);
      if (res?.success) {
        setMessage({ tone: "good", text: done });
        await load();
        return true;
      }
      setMessage({ tone: "bad", text: res?.error || "That didn't save." });
    } catch (e) {
      setMessage({ tone: "bad", text: e?.data?.error || "That didn't save." });
    } finally {
      setBusy(false);
    }
    return false;
  };

  const wallets = data?.wallets || [];
  const changes = data?.recentChanges || [];
  const existing = wallets.some((w) => w.accountId === form.accountId.trim());

  const save = async () => {
    const error = walletError(form);
    if (error) return setMessage({ tone: "bad", text: error });
    const accountId = form.accountId.trim();
    const ok = await send(saveUrl, { accountId, reason: form.reason, note: form.note.trim() },
      existing ? `${accountId} saved.` : `${accountId} added. What it holds no longer counts as customer money.`);
    if (ok) setForm(EMPTY_FORM);
  };

  const remove = async () => {
    if (!removing.note.trim()) return setMessage({ tone: "bad", text: "Enter a note." });
    const ok = await send(removeUrl, { accountId: removing.accountId, note: removing.note.trim() },
      `${removing.accountId} removed. What it holds counts as customer money again.`);
    if (ok) setRemoving(null);
  };

  const edit = (w) => {
    setForm({ accountId: w.accountId, reason: w.reason || "", note: w.note || "" });
    setMessage(null);
    document.getElementById("add-wallet")?.scrollIntoView({ behavior: "smooth" });
  };

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
      <div>
        <p style={{ ...note, margin: 0 }}>Accounts we own besides the treasuries, like fee and payout wallets. What they hold never counts as customer money.</p>
      </div>
      <button type="button" style={ghostBtn(loading)} disabled={loading} onClick={load}>{loading ? "Refreshing…" : "Refresh"}</button>
    </div>
  );

  if (!data) {
    return (
      <div>
        {header}
        {loading ? <p style={note}>Loading…</p> : <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      </div>
    );
  }

  return (
    <div>
      {header}
      {loadError && <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      <div style={card}>
        <h2 style={h2}>Wallets</h2>
        {wallets.length === 0 ? <p style={{ ...note, margin: 0 }}>No internal wallets yet. Add one below.</p> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={th}>Account</th><th style={th}>Reason</th><th style={th}>Note</th><th style={th}>Added by</th><th style={th}>Added</th><th style={th} /></tr>
              </thead>
              <tbody>
                {wallets.map((w) => (
                  <tr key={w.accountId}>
                    <td style={{ ...td, ...mono }}>{w.accountId}</td>
                    <td style={td}>{REASONS[w.reason] || w.reason}</td>
                    <td style={td}>{w.note || "-"}</td>
                    <td style={td}>{w.addedBy || "-"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{when(w.addedAt)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <button type="button" style={{ ...linkBtn, marginLeft: 0 }} onClick={() => edit(w)}>Edit</button>
                      <button type="button" style={linkBtn} onClick={() => { setRemoving({ accountId: w.accountId, note: "" }); setMessage(null); }}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {removing && (
          <div style={{ marginTop: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
            <p style={{ margin: "0 0 12px", fontSize: "14px" }}>Remove {removing.accountId}? What it holds counts as customer money again.</p>
            <div style={{ marginBottom: "12px" }}>
              <label style={label} htmlFor="remove-note">Note</label>
              <input id="remove-note" type="text" maxLength={500} style={{ ...input, maxWidth: "460px" }}
                value={removing.note} onChange={(e) => setRemoving({ ...removing, note: e.target.value })} />
              <div style={help}>Say why.</div>
            </div>
            <button type="button" style={btn(busy)} disabled={busy} onClick={remove}>Remove wallet</button>
            <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setRemoving(null)}>Cancel</button>
          </div>
        )}
      </div>

      <div style={card} id="add-wallet">
        <h2 style={h2}>{existing ? "Change a wallet" : "Add a wallet"}</h2>
        <p style={note}>Add an account we own. To change one, enter its account ID or pick Edit above.</p>
        <div style={grid}>
          <div>
            <label style={label} htmlFor="wallet-account">Account ID</label>
            <input id="wallet-account" type="text" placeholder="0.0.123456" style={input}
              value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value })} />
          </div>
          <div>
            <label style={label} htmlFor="wallet-reason">Reason</label>
            <select id="wallet-reason" style={input} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}>
              <option value="">Pick a reason</option>
              {Object.entries(REASONS).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
            </select>
          </div>
          <div>
            <label style={label} htmlFor="wallet-note">Note (optional)</label>
            <input id="wallet-note" type="text" maxLength={500} style={input}
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <button type="button" style={btn(busy)} disabled={busy} onClick={save}>{existing ? "Save changes" : "Add wallet"}</button>
        {(form.accountId || form.note || form.reason) && (
          <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setForm(EMPTY_FORM)}>Clear</button>
        )}
      </div>

      <div style={card}>
        <h2 style={h2}>Audit log</h2>
        {changes.length === 0 ? <p style={{ ...note, margin: 0 }}>No changes yet.</p> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>When</th><th style={th}>Account</th><th style={th}>Change</th><th style={th}>By</th><th style={th}>Note</th></tr></thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={c._id || `${c.at}-${i}`}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{when(c.at)}</td>
                    <td style={{ ...td, ...mono }}>{c.accountId}</td>
                    <td style={td}><strong>{ACTIONS[c.action] || c.action}</strong><div style={help}>{describeChange(c.before, c.after)}</div></td>
                    <td style={td}>{c.actor}</td>
                    <td style={td}>{c.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalWallets;
